"use client"

import {
  personDisplayName,
  useProfileById,
  useInviteToApply,
  getHrefWithThreadPreservation,
  lifecycleInviteAvailable,
  buildApplicantRankByApplicationId,
  usePersonMatchDetailTab,
} from '@yeshuman/sdk'
import { useMemo } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useOpportunity } from './use-organisation-opportunities'
import { useOpportunityApplicants } from './use-opportunity-applicants'
import { useJobProspectMatches } from './use-job-prospect-matches'
import {
  buildJobWorkspacePersonBackPath,
  type JobWorkspacePersonLocationState,
} from '../lib/job-workspace-person-paths'

function personPageDescription(
  isActiveApplicant: boolean,
  invited: boolean,
  hasProspect: boolean,
): string {
  if (isActiveApplicant) return 'Applicant for this role'
  if (invited) return 'Invited to apply'
  if (hasProspect) return 'Suggested match for this role'
  return 'Talent match for this role'
}

export function useJobWorkspacePersonPageState() {
  const { jobId = '', profileId = '' } = useParams<{ jobId: string; profileId: string }>()
  const location = useLocation()
  const personState = (location.state as JobWorkspacePersonLocationState | null) ?? {}
  const [searchParams] = useSearchParams()
  const evaluationIdFromQuery = searchParams.get('evaluation')
  const { data: opportunity, isLoading: jobLoading, error: jobError } = useOpportunity(jobId)
  const { profile, isLoading: profileLoading, error: profileError } = useProfileById(profileId || null)
  const { board, isLoading: boardLoading } = useOpportunityApplicants(jobId || undefined)
  const prospectsEnabled =
    !!jobId && lifecycleInviteAvailable(opportunity?.publication_status)
  const { data: prospectData, isLoading: prospectsLoading } = useJobProspectMatches(jobId, {
    enabled: prospectsEnabled,
  })
  const {
    inviteProfile,
    isApplied,
    isInvited,
    isInviting,
    invitingProfileId,
  } = useInviteToApply(jobId)

  const applicant = useMemo(
    () => board?.applicants.find((entry) => entry.profile.id === profileId) ?? null,
    [board?.applicants, profileId],
  )

  const prospect = useMemo(
    () => prospectData?.matches.find((match) => match.profile_id === profileId) ?? null,
    [prospectData?.matches, profileId],
  )

  const displayName = useMemo(() => {
    if (personState.label?.trim()) return personState.label.trim()
    if (profile) return personDisplayName(profile)
    return applicant?.profile.name || prospect?.name || 'Candidate'
  }, [applicant?.profile.name, personState.label, profile, prospect?.name])

  const jobListPath = useMemo(
    () =>
      personState.backPath ??
      buildJobWorkspacePersonBackPath(jobId, personState.origin ?? 'talent'),
    [jobId, personState.backPath, personState.origin],
  )

  const breadcrumbs = useMemo(() => {
    if (!jobId) return undefined
    return [
      { label: 'Jobs', to: '/employer/jobs' },
      {
        label: opportunity?.title ?? 'Job',
        to: jobListPath,
      },
      { label: displayName },
    ]
  }, [displayName, jobId, jobListPath, opportunity?.title])

  const applied = isApplied(profileId)
  const invited = isInvited(profileId)
  const isActiveApplicant = applicant != null && applicant.status !== 'invited'
  const trackingHref = applicant
    ? getHrefWithThreadPreservation(`/employer/jobs/${jobId}/applicants/${applicant.id}`)
    : null
  const evaluationId =
    applicant?.evaluation_id ?? prospect?.evaluation_id ?? evaluationIdFromQuery ?? undefined
  const inviteAvailable = opportunity
    ? lifecycleInviteAvailable(opportunity.publication_status)
    : false
  const showInviteAction = inviteAvailable && !applied

  const hasCoverLetter = Boolean(applicant?.has_cover_letter)
  const { activeTab, setActiveTab } = usePersonMatchDetailTab(evaluationId, hasCoverLetter)
  const stages = board?.stages ?? []

  const applicantRank = useMemo(() => {
    if (!applicant) return undefined
    return buildApplicantRankByApplicationId(board?.applicants ?? []).get(applicant.id)
  }, [applicant, board?.applicants])

  const isLoading =
    jobLoading || profileLoading || boardLoading || (prospectsEnabled && prospectsLoading)
  const loadError = jobError || profileError
  const missingRequired = !jobId || !profileId
  const missingData = loadError || !opportunity || !profile

  return {
    jobId,
    profileId,
    opportunity,
    profile,
    applicant,
    prospect,
    displayName,
    breadcrumbs,
    description: personPageDescription(isActiveApplicant, invited, !!prospect),
    isLoading,
    missingRequired,
    missingData,
    trackingHref,
    evaluationId,
    showInviteAction,
    applied,
    invited,
    isActiveApplicant,
    isInviting: isInviting && invitingProfileId === profileId,
    inviteProfile,
    hasAnalysis: Boolean(evaluationId),
    hasCoverLetter,
    activeTab,
    setActiveTab,
    stages,
    applicantRank,
  }
}
